/**
 * Authentication Hook
 *
 * Provides authentication state and utilities for protected routes.
 * Implements secure token validation and automatic cleanup.
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
}

export function useAuth(): AuthState {
  const [, setLocation] = useLocation();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    token: null,
  });

  useEffect(() => {
    // Validate auth token on mount
    const validateAuth = () => {
      try {
        const token = localStorage.getItem('auth_token');

        if (!token) {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            token: null,
          });
          return;
        }

        // Basic token validation - check if it's a non-empty string
        const isValid = typeof token === 'string' && token.length > 0;

        setAuthState({
          isAuthenticated: isValid,
          isLoading: false,
          token: isValid ? token : null,
        });

        // If token is invalid, clean up
        if (!isValid) {
          localStorage.removeItem('auth_token');
        }
      } catch (error) {
        // Handle corrupted localStorage
        console.error('Auth validation error:', error);
        localStorage.removeItem('auth_token');
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          token: null,
        });
      }
    };

    validateAuth();

    // Listen for storage events (multi-tab logout)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token') {
        if (!e.newValue) {
          // Token was removed - logout
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            token: null,
          });
          setLocation('/login');
        } else {
          // Token was added/updated
          validateAuth();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setLocation]);

  return authState;
}

/**
 * Logout utility function
 * Cleans up authentication state and redirects to login
 */
export function logout(): void {
  localStorage.removeItem('auth_token');
  // Trigger storage event for other tabs
  window.dispatchEvent(new StorageEvent('storage', {
    key: 'auth_token',
    oldValue: localStorage.getItem('auth_token'),
    newValue: null,
    url: window.location.href,
  }));
  window.location.href = '/login';
}
